import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, LogOut, Mail, Loader2, AlertTriangle } from 'lucide-react';
import { useTrialStatus } from '../../contexts/TrialStatusContext';
import { useAuth } from '../../contexts/AuthContext';
import { useBilling } from '../../contexts/BillingContext';
import { APP_CONFIG } from '../../config/appConfig';
import { goToManagedPricing } from '../../utils/shopifyBilling';

// Plan-ShopifyManagedPricingBilling.md (Phase 3 billing slice) — on the
// shopify build the trial-expired upgrade CTA routes to Shopify's hosted
// Managed Pricing page instead of /billing → Stripe checkout. Build-time
// constant so the stripe build keeps the original navigate('/billing')
// behavior untouched (and tree-shakes the Shopify path away).
const IS_SHOPIFY_BUILD = APP_CONFIG.billingProvider === 'shopify';

/**
 * TrialExpiredBlocker — full-screen blocker overlay for a workspace whose
 * trial has expired. Plan-TrialExpiration.md §C Phase 2.2.
 *
 * Sits over the main content panel only — the sidebar (z-50 in
 * Layout.tsx) is left untouched so the workspace switcher remains
 * interactive. We render at z-40 (below the sidebar) and offset the
 * left edge (`lg:pl-20` / `lg:pl-64` is handled by the parent main
 * panel; we stretch to `inset-0` and the parent's `lg:pl-*` on the
 * main content makes the blocker land inside that panel).
 *
 * Owner variant:
 *   - Heading + body explaining trial ended.
 *   - Primary CTA → /billing.
 *   - Secondary muted "Sign out" link.
 *
 * Non-owner variant:
 *   - Same heading.
 *   - Body names the workspace owner email (falls back to
 *     "an admin" when the members fetch hasn't landed).
 *   - NO primary CTA — /billing is owner-only anyway, and this UI
 *     respects that gate.
 *   - Secondary muted "Sign out" link.
 */
const TrialExpiredBlocker: React.FC = () => {
  const navigate = useNavigate();
  const { isOwner, ownerEmail, workspaceName } = useTrialStatus();
  const { logout } = useAuth();
  const { billing } = useBilling();

  const heading = 'Your trial has ended.';

  // Shopify build: the upgrade CTA resolves the hosted Managed Pricing
  // URL and redirects there. `redirecting` keeps the spinner up while the
  // window navigates; `notConfigured` shows a friendly message when the
  // store's billing isn't wired yet (backend 503).
  const [redirecting, setRedirecting] = useState(false);
  const [notConfigured, setNotConfigured] = useState(false);

  // Plan-SwitchToBillingAPI.md — backend tells us which Shopify flow to
  // use at runtime via `billing.billing_mode`. Matches the same branch
  // in ShopifyBillingView.tsx (commit 8cd341c). Defaults to `managed`
  // when the field is absent so pre-migration payloads keep the
  // existing hosted-redirect behavior.
  const billingMode: 'managed' | 'api' = billing?.billing_mode ?? 'managed';

  const handleUpgrade = async () => {
    // Stripe build: unchanged — go to the in-app billing page where the
    // Stripe plan-change/checkout flow lives.
    if (!IS_SHOPIFY_BUILD) {
      navigate('/billing');
      return;
    }
    // Shopify build + Billing API mode: the picker is now inline on
    // /billing (2026-05-31 unify ship retired /billing/choose-plan).
    // Each card handles its own checkout dispatch.
    if (billingMode === 'api') {
      navigate('/billing');
      return;
    }
    // Shopify build + Managed Pricing (or billing not loaded yet):
    // redirect to the hosted Managed Pricing page directly. We keep
    // this shortcut for `managed` mode because the hosted page is the
    // single CTA (no per-plan picker), so skipping the intermediate
    // /billing page is one less click for the trial-expired user.
    setRedirecting(true);
    setNotConfigured(false);
    const result = await goToManagedPricing();
    if (result.status === 'not_configured') {
      setNotConfigured(true);
      setRedirecting(false);
    } else if (result.status === 'error') {
      // Fall back to the in-app billing page so the owner is never stuck.
      setRedirecting(false);
      navigate('/billing');
    }
    // `redirecting` → window is navigating away; leave the spinner up.
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Trial expired"
      className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 sm:p-6"
    >
      {/*
        Card max-w-md on desktop; full-bleed (within the p-4 padding) on
        mobile.

        🚨 Do NOT put `glass` back here. `.glass` is
        `rgba(255,255,255,0.1)` in light mode, which over this dialog's
        `bg-black/60` backdrop composites to rgb(77,78,78) — an
        effectively DARK card. Every light-mode text colour on it then
        failed WCAG AA (heading 2.12:1, body 1.23:1, measured 2026-08-27).
        Dark mode was fine (`.dark .glass` is opaque enough), which is why
        this shipped. The card is therefore painted explicitly: opaque
        white in light, and `slate-900/60` in dark — byte-identical to
        what `.dark .glass` produced, so dark mode is unchanged.
      */}
      <div className="bg-white dark:bg-slate-900/60 backdrop-blur-md w-full max-w-md rounded-2xl border border-gray-200 dark:border-white/10 shadow-2xl p-6 sm:p-8">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-12 h-12 rounded-xl bg-red-100 dark:bg-red-900/30 flex items-center justify-center shrink-0">
            <Lock className="w-6 h-6 text-red-600 dark:text-red-400" aria-hidden="true" />
          </div>
          <div className="min-w-0">
            <h2 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white leading-tight">
              {heading}
            </h2>
            {workspaceName && (
              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                Workspace: {workspaceName}
              </p>
            )}
          </div>
        </div>

        {isOwner ? (
          <>
            <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed mb-6">
              Your trial has ended. Subscribe to a plan to reactivate
              this workspace and bring your agents back online.
            </p>
            {notConfigured && (
              <div className="rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 px-3 py-2.5 mb-4 text-xs text-amber-800 dark:text-amber-200 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" aria-hidden="true" />
                <span>
                  Billing isn&apos;t configured for this store yet. Please
                  check back shortly, or reach out to support.
                </span>
              </div>
            )}
            <button
              onClick={handleUpgrade}
              disabled={redirecting}
              className="w-full inline-flex items-center justify-center gap-2 bg-gradient-to-r from-primary to-purple-600 text-white font-semibold py-3 px-4 rounded-xl shadow-lg hover:shadow-xl hover:scale-[1.01] transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100"
            >
              {redirecting && <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />}
              Choose a plan
            </button>
          </>
        ) : (
          <>
            <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
              Your workspace owner needs to subscribe to a plan to reactivate
              this workspace. Please reach out to them.
            </p>
            {ownerEmail ? (
              <a
                href={`mailto:${ownerEmail}`}
                className="inline-flex items-center gap-2 text-sm text-primary dark:text-purple-400 hover:underline mb-6 break-all"
              >
                <Mail className="w-4 h-4 shrink-0" aria-hidden="true" />
                <span className="truncate">{ownerEmail}</span>
              </a>
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400 italic mb-6">
                Contact an admin in your workspace to upgrade.
              </p>
            )}
          </>
        )}

        {/*
          Escape hatch: a muted sign-out link so a user who landed on
          the wrong workspace (or simply wants to leave) is never
          trapped. Sidebar workspace switcher is the primary pivot
          path for multi-workspace users; this is the fallback.
        */}
        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-center">
          <button
            onClick={logout}
            className="inline-flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
          >
            <LogOut className="w-3.5 h-3.5" aria-hidden="true" />
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
};

export default TrialExpiredBlocker;
